import { inject, Injectable } from '@angular/core';
import {
  getSchemaText,
  initRawSchema,
  initVersionedSchema,
  toMentionContent,
  toTextContent,
} from '@principle-theorem/editor';
import {
  buildEmailContent,
  EditorPresetsService,
} from '@principle-theorem/ng-interactions';
import {
  CurrentBrandScope,
  GlobalStoreService,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  Appointment,
  Brand,
  ClinicalNote,
  Interaction,
  Invoice,
  isStafferRef,
  Media,
  Patient,
  PatientForm,
  Practice,
  toMention,
} from '@principle-theorem/principle-core';
import {
  CommunicationDirection,
  IBrand,
  type IInteractionV2,
  InteractionType,
  IPatient,
  MentionResourceType,
  PatientRelationshipType,
  type WithContext,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  Firestore,
  isDocRef,
  isPathChanged$,
  multiFilter,
  multiMap,
  multiSwitchMap,
  orderBy,
  reduce2DArray,
  shareReplayCold,
  snapshot,
  sortByCreatedAt,
  toMoment,
  toTimestamp,
  where,
  type WithRef,
} from '@principle-theorem/shared';
import { uniqBy } from 'lodash';
import { combineLatest, from, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PatientInteractionBuilderService {
  private _globalStore = inject(GlobalStoreService);
  private _brand$: Observable<WithRef<IBrand>>;

  constructor(
    private _brandScope: CurrentBrandScope,
    private _editorPresets: EditorPresetsService,
    private _organisation: OrganisationService
  ) {
    this._brand$ = this._brandScope.doc$.pipe(
      filterUndefined(),
      isPathChanged$('ref.id'),
      shareReplayCold()
    );
  }

  getLabJobInteractions$(
    patient: WithRef<IPatient>
  ): Observable<WithContext<IInteractionV2>[]> {
    return this._brand$.pipe(
      switchMap((brand) =>
        Patient.withPatientRelationships$(
          patient,
          [PatientRelationshipType.DuplicatePatient],
          (patientReffable) => Patient.labJobs$(patientReffable, brand)
        )
      ),
      multiMap((labJob) =>
        labJob.interactions.map((interaction) => ({
          ...Interaction.init(interaction),
          type: InteractionType.LabJob,
          suffix: [
            toTextContent('lab job '),
            toMentionContent(
              toMention(
                { name: getSchemaText(labJob.title), ref: labJob.ref },
                MentionResourceType.LabJob
              )
            ),
          ],
        }))
      ),
      reduce2DArray()
    );
  }

  getTasksInteractions$(
    patient: WithRef<IPatient>
  ): Observable<WithContext<IInteractionV2>[]> {
    return this._brand$.pipe(
      switchMap((brand) =>
        Patient.withPatientRelationships$(
          patient,
          [PatientRelationshipType.DuplicatePatient],
          (patientReffable) => Patient.tasks$(patientReffable, brand)
        )
      ),
      multiMap((task) =>
        task.interactions.map((interaction) => ({
          ...Interaction.init(interaction),
          type: InteractionType.Task,
          suffix: [
            toTextContent('task '),
            toMentionContent(
              toMention(
                { name: getSchemaText(task.title), ref: task.ref },
                MentionResourceType.Task
              )
            ),
          ],
        }))
      ),
      reduce2DArray()
    );
  }

  getClinicalNoteInteractions$(
    patient: WithRef<IPatient>
  ): Observable<WithContext<IInteractionV2>[]> {
    return Patient.withPatientRelationships$(
      patient,
      [PatientRelationshipType.DuplicatePatient],
      ClinicalNote.all$
    ).pipe(
      switchMap((notes) =>
        ClinicalNote.filterEmpty$(notes).pipe(
          multiMap((note) =>
            Interaction.init({
              uid: note.uid,
              content: note.content,
              owner: note.owner,
              createdAt: toTimestamp(toMoment(note.recordDate)),
              type: InteractionType.ClinicalNote,
              title: [
                toMentionContent(
                  toMention(note.owner, MentionResourceType.Staffer)
                ),
                toTextContent(' added clinical notes'),
              ],
              pinned: false,
              deleted: note.deleted,
            })
          )
        )
      )
    );
  }

  getAppointmentInteractions$(
    patient: WithRef<IPatient>
  ): Observable<WithContext<IInteractionV2>[]> {
    return Patient.withPatientRelationships$(
      patient,
      [PatientRelationshipType.DuplicatePatient],
      Appointment.all$
    ).pipe(
      multiFilter((appointment) => !appointment.deleted),
      multiSwitchMap((appointment) =>
        from(Appointment.summary(appointment)).pipe(
          switchMap((summary) =>
            Appointment.interactions$(appointment).pipe(
              multiMap((interaction) => ({
                ...interaction,
                type: InteractionType.Appointment,
                originalType: interaction.type,
                suffix: summary,
              }))
            )
          )
        )
      ),
      reduce2DArray()
    );
  }

  getPatientInteractions$(
    patient: WithRef<IPatient>
  ): Observable<WithContext<IInteractionV2>[]> {
    return Patient.withPatientRelationships$(
      patient,
      [PatientRelationshipType.DuplicatePatient],
      Patient.history$
    );
  }

  getInvoiceInteractions$(
    patient: WithRef<IPatient>
  ): Observable<WithContext<IInteractionV2>[]> {
    return Patient.withPatientRelationships$(
      patient,
      [PatientRelationshipType.DuplicatePatient],
      Patient.invoices$
    ).pipe(
      multiSwitchMap((invoice) => {
        const summary = Invoice.summary(invoice);
        return Invoice.interactions$(invoice).pipe(
          multiMap((interaction) => ({
            ...interaction,
            type: InteractionType.Invoice,
            originalType: interaction.type,
            suffix: summary,
          }))
        );
      }),
      reduce2DArray()
    );
  }

  getFormInteractions$(
    patient: WithRef<IPatient>
  ): Observable<WithContext<IInteractionV2>[]> {
    return Patient.withPatientRelationships$(
      patient,
      [PatientRelationshipType.DuplicatePatient],
      Patient.forms$
    ).pipe(
      multiSwitchMap((form) => {
        const summary = [
          toMentionContent(toMention(form, MentionResourceType.Form)),
        ];
        return PatientForm.interactions$(form).pipe(
          multiMap((interaction) => ({
            ...interaction,
            type: InteractionType.Form,
            originalType: interaction.type,
            suffix: summary,
          }))
        );
      }),
      reduce2DArray()
    );
  }

  getConversations$(
    patient: WithRef<IPatient>
  ): Observable<WithContext<IInteractionV2>[]> {
    return this._brand$.pipe(
      switchMap((brand) =>
        Patient.withPatientRelationships$(
          patient,
          [PatientRelationshipType.DuplicatePatient],
          (patientReffable) =>
            Brand.smsMessageByPatient$(brand, patientReffable)
        )
      ),
      multiFilter((message) => !!message.conversationRef),
      multiSwitchMap(async (message) => {
        const patientMention = toMentionContent(
          toMention(patient, MentionResourceType.Patient)
        );

        const interaction = Interaction.init({
          uid: message.ref.id,
          content: initVersionedSchema(message.body),
          type: InteractionType.Sms,
          createdAt: message.createdAt,
        });

        if (message.direction === CommunicationDirection.Inbound) {
          return {
            ...interaction,
            title: [patientMention, toTextContent(' replied via SMS')],
          };
        }

        const staffer =
          isDocRef(message.authorRef) && isStafferRef(message.authorRef)
            ? {
                name: await snapshot(
                  this._globalStore.getStafferName$({ ref: message.authorRef })
                ),
                ref: message.authorRef,
              }
            : undefined;

        const title = staffer
          ? [
              toMentionContent(toMention(staffer, MentionResourceType.Staffer)),
              toTextContent(' sent SMS to '),
              patientMention,
            ]
          : [toTextContent('SMS sent to '), patientMention];

        return {
          ...interaction,
          title,
          owner: staffer,
        };
      })
    );
  }

  getEmails$(
    patient: WithRef<IPatient>
  ): Observable<WithContext<IInteractionV2>[]> {
    return this._brand$.pipe(
      switchMap((brand) =>
        combineLatest([
          Patient.withPatientRelationships$(
            patient,
            [PatientRelationshipType.DuplicatePatient],
            (patientReffable) => Brand.emailByRecipient$(brand, patientReffable)
          ),
          Patient.withPatientRelationships$(
            patient,
            [PatientRelationshipType.DuplicatePatient],
            (patientReffable) => Brand.emailByAuthor$(brand, patientReffable)
          ),
        ])
      ),
      map(([receivedEmails, sentEmails]) => [...receivedEmails, ...sentEmails]),
      multiSwitchMap(async (message) => {
        const patientMention = toMentionContent(
          toMention(patient, MentionResourceType.Patient)
        );

        const interaction = Interaction.init({
          uid: message.ref.id,
          content: initRawSchema(
            buildEmailContent(message, this._editorPresets)
          ),
          type: InteractionType.Email,
          createdAt: message.createdAt,
        });

        const attachments = await Media.resolveEmailAttachments(
          message.attachments ?? [],
          message.ref
        );

        if (message.direction === CommunicationDirection.Inbound) {
          return {
            ...interaction,
            attachments,
            title: [patientMention, toTextContent(' replied via email')],
          };
        }

        const staffer =
          isDocRef(message.authorRef) && isStafferRef(message.authorRef)
            ? {
                name: await snapshot(
                  this._globalStore.getStafferName$({ ref: message.authorRef })
                ),
                ref: message.authorRef,
              }
            : undefined;

        const title = staffer
          ? [
              toMentionContent(toMention(staffer, MentionResourceType.Staffer)),
              toTextContent(' sent email to '),
              patientMention,
            ]
          : [toTextContent('Automated email sent to '), patientMention];

        return {
          ...interaction,
          attachments,
          title,
          owner: staffer,
        };
      })
    );
  }

  getCallEventInteractions$(
    patient: WithRef<IPatient>
  ): Observable<WithContext<IInteractionV2>[]> {
    return this._organisation.practices$.pipe(
      multiSwitchMap((practice) => {
        const patientEvents$ = Firestore.query$(
          Practice.callEventCol(practice),
          where('context.patientRef', '==', patient.ref),
          orderBy('createdAt', 'desc')
        );
        const leadEvents$ = Firestore.query$(
          Practice.callEventCol(practice),
          where('context.leadRef', '==', patient.ref),
          orderBy('createdAt', 'desc')
        );

        return combineLatest([patientEvents$, leadEvents$]).pipe(
          reduce2DArray()
        );
      }),
      reduce2DArray(),
      map((callEvents) =>
        uniqBy([...callEvents], (event) => event.ref.path).sort(sortByCreatedAt)
      ),
      multiSwitchMap((event) =>
        this._globalStore.getCallEventDisplay$(event).pipe(
          map((display) => ({
            ...Interaction.init({
              uid: event.ref.id,
              type: InteractionType.CallEvent,
              createdAt: event.createdAt,
              title: [
                toTextContent(
                  `${display.label} call — ${
                    event.callerName ?? event.callerPhone ?? 'Unknown caller'
                  }`
                ),
              ],
              content: initVersionedSchema(
                event.summary ?? 'No summary provided — view transcript'
              ),
              pinned: false,
              deleted: false,
            }),
            callEvent: event,
          }))
        )
      )
    );
  }
}
